import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Plus, Trash2, Flame, Loader2 } from "lucide-react";
import { youtubeId } from "../lib/video";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");

// Coach tool: post Elite-only video tips to the Private Locker Room feed.
export default function LockerRoomManager({ token }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [posts, setPosts] = useState([]);
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/coach/locker-posts`, auth).then(({ data }) => setPosts(data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const post = async () => {
    if (!title.trim()) { toast.error("Give the post a title."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/coach/locker-posts`, { title, video_url: url, caption }, auth);
      setTitle(""); setUrl(""); setCaption("");
      toast.success("Posted to the Elite Locker Room.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't post."); }
    finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}" from the Locker Room?`)) return;
    try { await axios.delete(`${API}/coach/locker-posts/${p.id}`, auth); load(); toast.success("Post removed."); }
    catch { toast.error("Couldn't delete."); }
  };

  const totalFire = posts.reduce((s, p) => s + (p.fire_count || 0), 0);

  return (
    <div data-testid="locker-room-manager" className="bg-[#141414] border border-[#D4AF37]/25 rounded-lg p-6 mb-8">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2"><Lock size={16} className="text-[#D4AF37]" /><h2 className="font-subheadings text-sm font-bold uppercase tracking-widest">Private Locker Room · Elite Feed</h2></div>
        {posts.length > 0 && <span className="text-[10px] font-bold uppercase tracking-wider text-white/45 flex items-center gap-1"><Flame size={11} className="text-[#C41E24]" /> {totalFire} reactions · {posts.length} posts</span>}
      </div>
      <p className="text-sm text-white/50 mb-4">Post Elite-only video tips. They appear at the bottom of every Elite member's Hub; Technical Track &amp; Blueprint players see a locked teaser that upsells Elite.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <input data-testid="locker-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title — e.g. Elastico: the hidden hip cue"
          className="w-full bg-[#0d0d0d] border border-white/10 rounded-md p-2.5 text-white text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]" />
        <input data-testid="locker-url" type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="YouTube link (unlisted works great)"
          className="w-full bg-[#0d0d0d] border border-white/10 rounded-md p-2.5 text-white text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]" />
      </div>
      <textarea data-testid="locker-caption" rows={2} value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Caption — what should they look for, and how many reps this week?"
        className="w-full bg-[#0d0d0d] border border-white/10 rounded-md p-2.5 text-white text-sm resize-y focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37] mb-3" />
      <button data-testid="locker-post-btn" onClick={post} disabled={busy}
        className="flex items-center gap-2 bg-gradient-to-r from-[#E7C64B] to-[#C39A2B] hover:brightness-110 text-black text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-sm transition-all disabled:opacity-60">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Post to Locker Room
      </button>

      {posts.length > 0 && (
        <div className="mt-5 space-y-2" data-testid="locker-post-list">
          {posts.map((p) => {
            const yid = youtubeId(p.video_url);
            return (
              <div key={p.id} data-testid={`locker-manager-post-${p.id}`} className="flex items-center gap-3 bg-[#0d0d0d] border border-white/10 rounded-md p-3">
                {yid ? <img loading="lazy" decoding="async" src={`https://img.youtube.com/vi/${yid}/mqdefault.jpg`} alt="" className="w-20 h-12 object-cover rounded-sm shrink-0" /> : <div className="w-20 h-12 rounded-sm bg-white/5 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{p.title}</div>
                  <div className="text-[11px] text-white/40 truncate">{fmt(p.created_at)}{p.caption ? ` · ${p.caption}` : ""}</div>
                </div>
                <span className="text-xs font-bold text-white/60 flex items-center gap-1 shrink-0"><Flame size={12} className="text-[#C41E24]" /> {p.fire_count}</span>
                <button data-testid={`locker-delete-${p.id}`} onClick={() => remove(p)} className="text-white/30 hover:text-[#C41E24] transition-colors p-1" aria-label="Delete post"><Trash2 size={15} /></button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
